
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function PartnerPrivacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Partner Privacy Policy</h1>
            
            <div className="text-gray-600 space-y-8">
              <div>
                <p className="text-sm text-gray-500 mb-4">Last updated: January 2024</p>
                <p className="text-lg mb-4">
                  At Baainearme (BNM), we respect your privacy as a service partner. This Privacy Policy explains how we collect, use, and protect your information as you provide services through our platform.
                </p>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Information We Collect</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Personal and Professional Information</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Full name, email address, and phone number</li>
                      <li>Home address and service area preferences</li>
                      <li>Professional skills and service categories</li>
                      <li>Work experience and qualifications</li>
                      <li>Bank account details for payments</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Verification Documents</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Aadhaar card and PAN card details</li>
                      <li>Bank account verification documents</li>
                      <li>Professional certifications (if applicable)</li>
                      <li>Background verification reports</li>
                      <li>Photo identification and profile pictures</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Service Activity Data</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      <li>Service requests accepted and completed</li>
                      <li>Customer ratings and reviews</li>
                      <li>Earnings and payment history</li>
                      <li>Location data during service delivery</li>
                      <li>Communication with customers and support</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>To verify your identity and professional qualifications</li>
                  <li>To match you with appropriate service requests</li>
                  <li>To process payments and manage your earnings</li>
                  <li>To facilitate communication with customers</li>
                  <li>To monitor service quality and performance</li>
                  <li>To provide training and support resources</li>
                  <li>To comply with legal and regulatory requirements</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Information Sharing</h2>
                <div className="space-y-4">
                  <p>We share your information in the following circumstances:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li><strong>Customers:</strong> We share your name, photo, ratings, and contact details with customers for service bookings</li>
                    <li><strong>Payment Processing:</strong> Bank details shared with payment processors for earnings transfer</li>
                    <li><strong>Background Verification:</strong> Information shared with verification agencies for safety checks</li>
                    <li><strong>Legal Authorities:</strong> When required by law or for safety investigations</li>
                    <li><strong>Business Operations:</strong> In case of merger, acquisition, or business transfer</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Partner Profile Visibility</h2>
                <div className="space-y-4">
                  <p>Your profile information visible to customers includes:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Profile photo and name</li>
                    <li>Service categories and skills</li>
                    <li>Average ratings and customer reviews</li>
                    <li>Years of experience</li>
                    <li>Verified status and badges</li>
                  </ul>
                  <p className="text-sm text-gray-500 mt-4">
                    Note: Personal contact information is only shared after booking confirmation.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Data Security</h2>
                <div className="space-y-4">
                  <p>We protect your sensitive information through:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Encryption of financial and identity documents</li>
                    <li>Secure storage of verification documents</li>
                    <li>Regular security audits and system updates</li>
                    <li>Limited access to personal information</li>
                    <li>Secure communication channels for support</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Rights and Control</h2>
                <p className="mb-4">As a service partner, you have the right to:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li><strong>Profile Control:</strong> Update your profile information and service preferences</li>
                  <li><strong>Data Access:</strong> Request a copy of all data we hold about you</li>
                  <li><strong>Data Correction:</strong> Update or correct any inaccurate information</li>
                  <li><strong>Account Deletion:</strong> Request permanent deletion of your partner account</li>
                  <li><strong>Communication Preferences:</strong> Control marketing and notification settings</li>
                  <li><strong>Earnings Data:</strong> Access detailed earnings and payment history</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Location and Tracking</h2>
                <div className="space-y-4">
                  <p>We collect location data to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Match you with nearby service requests</li>
                    <li>Provide customers with real-time service tracking</li>
                    <li>Verify service completion at customer locations</li>
                    <li>Optimize service routing and efficiency</li>
                  </ul>
                  <p className="text-sm text-gray-500 mt-4">
                    Location tracking can be disabled when you're not actively providing services.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Earnings and Financial Data</h2>
                <div className="space-y-4">
                  <p>We maintain detailed records of:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Service fees and commission calculations</li>
                    <li>Weekly payout statements and tax information</li>
                    <li>Bonus and incentive payments</li>
                    <li>Transaction history and payment methods</li>
                  </ul>
                  <p className="text-sm text-gray-500 mt-4">
                    Financial data is encrypted and stored securely for tax and regulatory compliance.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Contact Us</h2>
                <p className="mb-4">For privacy-related questions or to exercise your rights:</p>
                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="space-y-2">
                    <p><strong>Email:</strong> partner-privacy@baainearme.com</p>
                    <p><strong>Phone:</strong> 93074 19845</p>
                    <p><strong>Address:</strong> BNM Headquarters, Nagpur, Maharashtra, India</p>
                    <p><strong>Partner Support Hours:</strong> Monday-Saturday, 9 AM - 6 PM</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy to reflect changes in our practices or legal requirements. Partners will be notified of significant changes via email or through the partner app. Continued use of our platform constitutes acceptance of the updated policy.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 420 160"
  className="h-10 text-white"   // change to text-purple-600 etc.
  role="img"
  aria-label="BNM · Baainearme"
>
  <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
    {/* Left ring */}
    <circle cx="50" cy="60" r="40" />
    {/* Vertical divider */}
    <line x1="110" y1="20" x2="110" y2="120" />
  </g>

  {/* Text block */}
  <g fill="currentColor">
    {/* BNM */}
    <text
      x="140"
      y="72"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="800"
      fontSize="56"
    >
      BNM
    </text>

    {/* Baainearme */}
    <text
      x="140"
      y="120"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="700"
      fontSize="32"
    >
      Baainearme
    </text>
  </g>
</svg>

              <p className="text-gray-400">
                Empowering service providers with flexible earning opportunities
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Partners</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/partner" className="hover:text-white cursor-pointer">Partner Home</Link></li>
                <li><Link to="/partner-privacy" className="hover:text-white cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/partner-terms" className="hover:text-white cursor-pointer">Terms & Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer" className="hover:text-white cursor-pointer">For Customers</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>📞 93074 19845</li>
                <li>✉ support@baainearme.com</li>
                <li><a href="#" className="hover:text-white cursor-pointer">Partner Support</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
